import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader } from '@repo/ui';
import { CustomFieldManagement } from './CustomFieldManagement';
import { CustomFieldApplication } from './CustomFieldApplication';
import { CustomFieldStatusCheck } from './CustomFieldStatusCheck';
import { api } from '@xtnd/web/shared/api';

interface ClioCustomField {
  id: number;
  etag: string;
  field_name: string;
  name: string;
  field_type: 'text' | 'number' | 'date' | 'checkbox' | 'dropdown' | 'textarea';
  field_required: boolean;
  field_displayed: boolean;
  field_display_order: number[];
  parent_type: 'Matter' | 'Contact' | 'Activity';
  created_at: string;
  updated_at: string;
  soft_deleted?: boolean;
  dropdown_options?: string[];
  picklist_options?: Array<{
    id: number;
    option: string;
  }>;
}

interface CustomFieldManagerProps {
  expectedFieldName?: string;
}

export function CustomFieldManager({
  expectedFieldName = 'Enable Juniper Processing',
}: CustomFieldManagerProps) {
  // Fetch all custom fields to check if expected field exists
  const { data: customFields = [], refetch: refetchFields } = useQuery<ClioCustomField[]>({
    queryKey: ['clio-all-custom-fields'],
    queryFn: async () => {
      const response = await api.get<{ data: ClioCustomField[] }>('/clio/custom-fields');
      return response.data || [];
    },
    retry: 2,
    retryDelay: 1000,
  });

  // Check if expected field exists
  const expectedField = customFields.find(
    field => field.name === expectedFieldName || field.field_name === expectedFieldName
  );

  const fieldExists = !!expectedField;
  const fieldId = expectedField?.id || 0;

  return (
    <div className="space-y-6">
      <Card className="border-xtnd-light">
        <CardHeader>
          <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
            Custom Field Management
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage the "{expectedFieldName}" custom field for Juniper processing
          </p>
        </CardHeader>
      </Card>

      {/* Custom Field Management - Display all fields and create if needed */}
      <CustomFieldManagement expectedFieldName={expectedFieldName} />

      {/* Custom Field Application - Apply field to matters */}
      <CustomFieldApplication
        fieldName={expectedFieldName}
        fieldId={fieldId}
        enabled={fieldExists}
      />

      {/* Custom Field Status Check - Check status across matters */}
      <CustomFieldStatusCheck
        fieldName={expectedFieldName}
        fieldId={fieldId}
        enabled={fieldExists}
      />
    </div>
  );
}
